import { camelCase } from 'lib/utils/objectSerializer';
import format from 'lib/store/orders/formatter';

import {
  API_SUBMIT_REFUND_DISPUTE_REQUEST,
  API_SUBMIT_REFUND_DISPUTE_SUCCESS,
  API_SUBMIT_REFUND_DISPUTE_FAILURE,
  API_GET_AVAILABLE_DISPUTE_REASONS_REQUEST,
  API_GET_AVAILABLE_DISPUTE_REASONS_SUCCESS,
  API_GET_AVAILABLE_DISPUTE_REASONS_FAILURE,
  API_FETCH_DISPUTE_STATUS_FOR_ORDER_REQUEST,
  API_FETCH_DISPUTE_STATUS_FOR_ORDER_SUCCESS,
  API_FETCH_DISPUTE_STATUS_FOR_ORDER_FAILURE,
  API_FETCH_REFUND_DISPUTES_REQUEST,
  API_FETCH_REFUND_DISPUTES_SUCCESS,
  API_FETCH_REFUND_DISPUTES_FAILURE,
  UPDATE_SELECTED_DISPUTE_REASONS,
  COMBINE_ORDER_AND_REFUNDED_ORDER,
  RESET_SELECTED_DISPUTE_REASONS,
  UPDATE_CURRENT_ORDER_ID,
  SET_REFUND_DISPUTE_SUBMISSION_FAILED,
} from './actionTypes';
import { RefundDisputesAction } from './actions';

import {
  AvailableDisputeReasons,
  RefundDispute,
  SelectedDisputeReason,
  LoadingType,
  OrderType,
} from './types';

export type RefundDisputesState = {
  availableDisputeReasons?: AvailableDisputeReasons;
  disputeStatusForOrder?: RefundDispute;
  selectedDisputeReasons: SelectedDisputeReason[];
  currentOrderId?: string;
  refundDisputes?: RefundDispute[];
  disputeDetails?: {
    [orderId: string]: {
      order?: any;
      refundedOrder?: any;
      customerFrequency?: any;
      combinedOrder?: OrderType;
    };
  };
  loading: LoadingType;
  submittedDisputes: { [id: string]: boolean };
};

const initialState = {
  availableDisputeReasons: undefined,
  disputeStatusForOrder: undefined,
  selectedDisputeReasons: [],
  currentOrderId: undefined,
  refundDisputes: undefined,
  disputeDetails: undefined,
  loading: {
    availableDisputeReasons: false,
    disputeStatusForOrder: false,
    submitRefundDispute: false,
    refundDisputes: false,
    customerFrequency: false,
  },
  submittedDisputes: {},
};

export default function refundDisputesReducer(
  state: RefundDisputesState = initialState,
  action: RefundDisputesAction,
): RefundDisputesState {
  switch (action.type) {
    case API_SUBMIT_REFUND_DISPUTE_REQUEST:
      return {
        ...state,
        disputeStatusForOrder: undefined,
        loading: {
          ...state.loading,
          submitRefundDispute: true,
        },
      };

    case API_SUBMIT_REFUND_DISPUTE_SUCCESS:
      // eslint-disable-next-line no-case-declarations
      const submittedDisputes = state.submittedDisputes;
      submittedDisputes[action.payload.order.metricsOrderUuid] = true;
      return {
        ...state,
        disputeStatusForOrder: camelCase(action.payload),
        loading: {
          ...state.loading,
          submitRefundDispute: false,
        },
        submittedDisputes,
      };

    case SET_REFUND_DISPUTE_SUBMISSION_FAILED:
      // eslint-disable-next-line no-case-declarations
      const oldSubmittedDisputes = state.submittedDisputes;
      oldSubmittedDisputes[action.payload.orderId] = false;
      return {
        ...state,
        submittedDisputes: oldSubmittedDisputes,
      };

    case API_SUBMIT_REFUND_DISPUTE_FAILURE:
      return {
        ...state,
        disputeStatusForOrder: undefined,
        loading: {
          ...state.loading,
          submitRefundDispute: false,
        },
      };

    case API_GET_AVAILABLE_DISPUTE_REASONS_REQUEST:
      return {
        ...state,
        availableDisputeReasons: undefined,
        loading: {
          ...state.loading,
          availableDisputeReasons: true,
        },
      };

    case API_GET_AVAILABLE_DISPUTE_REASONS_SUCCESS:
      return {
        ...state,
        availableDisputeReasons: action.payload,
        loading: {
          ...state.loading,
          availableDisputeReasons: false,
        },
      };

    case API_GET_AVAILABLE_DISPUTE_REASONS_FAILURE:
      return {
        ...state,
        availableDisputeReasons: undefined,
        loading: {
          ...state.loading,
          availableDisputeReasons: false,
        },
      };

    case API_FETCH_DISPUTE_STATUS_FOR_ORDER_REQUEST:
      return {
        ...state,
        disputeStatusForOrder: undefined,
        loading: {
          ...state.loading,
          disputeStatusForOrder: true,
        },
      };

    case API_FETCH_DISPUTE_STATUS_FOR_ORDER_SUCCESS:
      return {
        ...state,
        disputeStatusForOrder: camelCase(action.payload),
        loading: {
          ...state.loading,
          disputeStatusForOrder: false,
        },
      };

    case API_FETCH_DISPUTE_STATUS_FOR_ORDER_FAILURE:
      return {
        ...state,
        disputeStatusForOrder: undefined,
        loading: {
          ...state.loading,
          disputeStatusForOrder: false,
        },
      };

    case API_FETCH_REFUND_DISPUTES_REQUEST:
      return {
        ...state,
        refundDisputes: undefined,
        loading: {
          ...state.loading,
          refundDisputes: true,
        },
      };

    case API_FETCH_REFUND_DISPUTES_SUCCESS:
      return {
        ...state,
        refundDisputes: camelCase(action.payload),
        loading: {
          ...state.loading,
          refundDisputes: false,
        },
      };

    case API_FETCH_REFUND_DISPUTES_FAILURE:
      return {
        ...state,
        refundDisputes: undefined,
        loading: {
          ...state.loading,
          refundDisputes: false,
        },
      };

    case 'GET_ORDER_DETAILS_SUCCESS': {
      const order = format.order(action.payload);

      const rest = (state: RefundDisputesState) => {
        if (state.disputeDetails && state.disputeDetails[order.id])
          return { ...state.disputeDetails[order.id] };

        return {};
      };

      return {
        ...state,
        disputeDetails: {
          ...state.disputeDetails,
          [order.id]: {
            ...rest(state),
            order,
          },
        },
      };
    }

    case 'GET_REFUNDED_ORDER_DETAILS_SUCCESS': {
      const refundedOrder = format.order(action.payload);

      const rest = (state: RefundDisputesState) => {
        if (state.disputeDetails && state.disputeDetails[refundedOrder.uuid])
          return { ...state.disputeDetails[refundedOrder.uuid] };

        return {};
      };

      return {
        ...state,
        disputeDetails: {
          ...state.disputeDetails,
          [refundedOrder.uuid]: {
            ...rest(state),
            refundedOrder,
          },
        },
      };
    }

    case 'GET_CUSTOMER_FREQUENCY_REQUEST':
      // only update the state if we call this from the `OrderDetails` section
      if (!action.extra.orderDrnId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        loading: {
          ...state.loading,
          customerFrequency: true,
        },
      };

    case 'GET_CUSTOMER_FREQUENCY_SUCCESS': {
      // only update the state if we call this from the `OrderDetails` section
      if (!action.extra.orderDrnId) {
        return {
          ...state,
        };
      }

      const rest = (state: RefundDisputesState) => {
        if (
          state.disputeDetails &&
          state.disputeDetails[action.extra.orderDrnId]
        )
          return { ...state.disputeDetails[action.extra.orderDrnId] };

        return {};
      };

      return {
        ...state,
        disputeDetails: {
          ...state.disputeDetails,
          [action.extra.orderDrnId]: {
            ...rest(state),
            customerFrequency: {
              frequency: action.payload.customer_orders,
            },
          },
        },
        loading: {
          ...state.loading,
          customerFrequency: false,
        },
      };
    }

    case RESET_SELECTED_DISPUTE_REASONS: {
      return {
        ...state,
        selectedDisputeReasons: [],
      };
    }

    case UPDATE_CURRENT_ORDER_ID: {
      return {
        ...state,
        currentOrderId: action.orderId,
      };
    }

    case UPDATE_SELECTED_DISPUTE_REASONS: {
      const objectIndex = state.selectedDisputeReasons.findIndex(
        (selectedDisputeReason: SelectedDisputeReason) =>
          selectedDisputeReason.index === action.index,
      );

      const updateSelectedDisputeReason = () => {
        const newSelectedDisputeReasonsArray = [
          ...state.selectedDisputeReasons,
        ];

        newSelectedDisputeReasonsArray[objectIndex].disputeReason =
          action.values[1];

        return newSelectedDisputeReasonsArray;
      };

      return {
        ...state,
        selectedDisputeReasons:
          // eslint-disable-next-line no-nested-ternary
          objectIndex === -1
            ? [
                ...state.selectedDisputeReasons,
                {
                  index: action.index,
                  itemId: action.values[0],
                  disputeReason:
                    action.inputType === 'SELECT' ? action.values[1] : '',
                  compensationId: action.compensationId,
                },
              ]
            : action.inputType === 'CHECKBOX'
            ? state.selectedDisputeReasons.filter(
                (selectedDisputeReason: SelectedDisputeReason) =>
                  selectedDisputeReason.index !== action.index,
              )
            : updateSelectedDisputeReason(),
      };
    }

    case COMBINE_ORDER_AND_REFUNDED_ORDER: {
      const rest = (state: RefundDisputesState) => {
        if (
          state.disputeDetails &&
          state.disputeDetails[action.selectedOrderDrnId]
        )
          return { ...state.disputeDetails[action.selectedOrderDrnId] };

        return {};
      };

      /**
       * The following functions were taken from `lib/pages/orders/OrderDetail/index.js`
       * Modified to make TS happy and suit this usecase
       *
       * !note: this will probably be removed once the API returns this object for each dispute
       */
      const omitNull = (obj: any) => {
        const newObj = obj;

        Object.keys(newObj).forEach((key) => {
          if (Array.isArray(newObj[key])) {
            newObj[key].map((item: any) => omitNull(item));
          }
          if (newObj[key] === undefined) {
            // eslint-disable-next-line no-param-reassign
            delete newObj[key];
          }
        });

        return newObj;
      };

      const mergeArrayItem = (obj1: any, obj2: any) => {
        return { ...obj1, ...obj2 };
      };

      const merge = (state: RefundDisputesState) => {
        const order = omitNull(
          state.disputeDetails![action.selectedOrderDrnId].order,
        );
        const refundedOrder = omitNull(
          state.disputeDetails![action.selectedOrderDrnId].refundedOrder,
        );

        const items = [];
        if (order.items && refundedOrder.items) {
          const orderItems = order.items;
          const refundedOrderItems = refundedOrder.items;
          for (let i = 0; i < orderItems.length; i++) {
            items[i] = mergeArrayItem(orderItems[i], refundedOrderItems[i]);
          }
          refundedOrder.items = items;
        }

        return { ...order, ...refundedOrder };
      };
      // ------------------------------------------

      return {
        ...state,
        disputeDetails: {
          ...state.disputeDetails,
          [action.selectedOrderDrnId]: {
            ...rest(state),
            combinedOrder: merge(state),
          },
        },
      };
    }

    default:
      return state;
  }
}
